import { Form, Input, notification, Spin } from 'antd';
import _ from 'lodash';
import { useState } from 'react';
import { Link, withRouter } from 'react-router-dom';
import styled from 'styled-components';

import OktaLogo from 'v3/assets/okta-logo.png';
import MicrosoftLogo from 'v3/assets/294669_microsoft_icon.png';
import OktaAuthService from './OktaAuthService';

import MicrosoftAuthService from './MicrosoftAuthService';

import { CurrentUserStore, ShopStore } from 'stores';
import { ErrorField } from 'v3/components/pitstop';
import { isProduction } from 'shared/customFleets';

const messages = [
  'Scan work orders instantly with Pitstop AI.',
  'Catch issues early. Save time now.',
  'Smarter maintenance starts here today.',
  'Optimize fault priorities in real-time.',
  'Discover root causes with Pitstop AI insights.',
  'Save money and keep fleets moving.',
  'Digitize, automate, and stay ahead.',
  'Invite your team to smarter workflows.',
  'Streamline maintenance with Pitstop AI.',
  'Your Pitstop AI assistant is ready to help.',
];

const getRandomMessage = () => {
  return messages[Math.floor(Math.random() * messages.length)];
};

const Container = styled(Form)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1.5rem;

  h1 {
    font-size: 2.8rem;
    font-weight: 600;
    color: #000000;
  }

  p {
    text-align: center;
    font-size: 1.2rem;
    color: #8c8c8c;
    margin: 0;
  }
`;

const DashLine = styled.div`
  width: 100%;
  border-top: 2px dashed #ccc;
  opacity: 0.5;
  margin: 1rem 0;
`;

const LoginButton = styled.button`
  cursor: pointer;
  border: none;
  background: #02103d;
  color: #ffffff;
  border-radius: 5px;
  height: 3rem;
  min-width: 8rem;
  font-size: 1rem;
  font-weight: bold;
  margin-top: 2vh;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
`;

const OktaButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  background: white;
  border-radius: 5px;
  height: 3rem;
  min-width: 17rem;
  font-weight: bold;
  font-size: 1rem;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));

  img {
    margin-left: 8px;
    height: 24px;
  }

  &:hover {
    background: #f3f3f3;
  }
`;

// Add a Microsoft button with identical styles
const MicrosoftButton = styled(OktaButton)``;

const LoginForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    isSubmitted: false,
    randomMessage: getRandomMessage(),
    apiError: null,
  });

  const { email, password, isSubmitted, randomMessage, apiError } = formData;

  const getError = (type, value) => {
    if (_.isEmpty(value)) {
      return <ErrorField>{`${type} is required!`}</ErrorField>;
    }
    if (type === 'email') {
      const emailRegex = /^\S+@\S+\.\S+$/;
      if (!emailRegex.test(value) && !/^[a-zA-Z0-9_.-]+$/.test(value)) {
        return <ErrorField>Enter a valid username or email address</ErrorField>;
      }
    }
    if (type === 'password' && value.length < 6) {
      return <ErrorField>Password must be at least 6 characters</ErrorField>;
    }
    return null;
  };

  const onChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
      apiError: null,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormData((prev) => ({ ...prev, isSubmitted: true }));

    if (getError('email', email) || getError('password', password)) return;

    notification.open({
      key: 'login',
      description: (
        <>
          <Spin size="large" style={{ marginRight: '0.6rem' }} />
          Logging you into Pitstop dashboard...
        </>
      ),
      duration: 0,
    });

    try {
      ShopStore.reset();
      await CurrentUserStore.login(email.trim(), password.trim());
      await CurrentUserStore.getCurrentUser();
    } catch (err) {
      setFormData((prev) => ({
        ...prev,
        apiError:
          'The email and password combination you entered doesn\'t match any account. Please try again or register a new account.',
      }));
      notification.error({ message: 'Error', description: err.message });
    } finally {
      notification.destroy();
    }
  };

  const handleOktaLogin = async () => {
    try {
      const authUrl = await OktaAuthService.getAuthorizationUrl();
      window.location.href = authUrl;
    } catch {
      notification.error({
        message: 'Error generating Okta authorization URL',
      });
    }
  };

  const handleMicrosoftLogin = async () => {
    try {
      const authUrl = await MicrosoftAuthService.getAuthorizationUrl();
      window.location.href = authUrl;
    } catch (err) {
      console.log(err);
      notification.error({
        message: 'Error generating Microsoft authorization URL',
      });
    }
  };

  return (
    <Container>
      <h1>Welcome Back!</h1>
      <p>{randomMessage}</p>
      <Input
        name="email"
        placeholder="Work email (e.g., name@company.com)"
        onChange={onChange}
        data-testid="login-email-input"
      />
      {isSubmitted && getError('email', email)}
      <Input
        name="password"
        type="password"
        placeholder="Password (min. 6 characters)"
        onChange={onChange}
        data-testid="login-password-input"
      />
      {isSubmitted && getError('password', password)}
      {apiError && <ErrorField>{apiError}</ErrorField>}
      <LoginButton onClick={onSubmit} data-testid="login-button">
        Login
      </LoginButton>
      <Link
        to="/reset-password"
        style={{ marginTop: '1rem', color: '#02103d', fontWeight: '600' }}
      >
        Forgot password?
      </Link>
      <DashLine />
      <OktaButton onClick={handleOktaLogin}>
        <span>Continue with</span>
        <img src={OktaLogo} alt="Okta" />
      </OktaButton>
      <MicrosoftButton onClick={handleMicrosoftLogin}>
        <span>Continue with</span>
        <img src={MicrosoftLogo} alt="Microsoft" style={{ height: '50px' }} />
      </MicrosoftButton>
    </Container>
  );
};

export default withRouter(LoginForm);
